import { createActionCreators } from 'immer-reducer';

import MainProtected, { RequisiteBody } from '@/api/main-protected';
import {
  Currency, ExpenseAttachment, PayoutLine, PayoutReducer,
} from '@/store/reducers/payout';
import { forgetAttachment } from '@/utils/attachments';
import { revalidate } from '@/store/responses';
import { AsyncAction } from './common';

export const payoutActions = createActionCreators(PayoutReducer);

export type PayoutActions =
  | ReturnType<typeof payoutActions.setPayout>
  | ReturnType<typeof payoutActions.setSalary>
  | ReturnType<typeof payoutActions.setSuggestions>
  | ReturnType<typeof payoutActions.setGrid>
  | ReturnType<typeof payoutActions.setRequisites>
  | ReturnType<typeof payoutActions.cleanPayout>;

// Which payout the screen is looking at. Personal screens pass no userId, the
// admin screens pass one; every mutation takes it back so the refetch lands on
// the same slice the screen is rendering.
export interface PayoutTarget {
  userId?: number;
  period?: string;
  onError?: () => void;
}

export const getPayout = (
  { userId, period }: PayoutTarget = {},
): AsyncAction => async (dispatch, _, { mainProtectedApi }) => {
  try {
    const path = userId ? `/payout/user/${userId}` : '/payout';

    await revalidate(
      period ? `${path}?period=${period}` : path,
      () => (userId
        ? mainProtectedApi.getUserPayout(userId, period)
        : mainProtectedApi.getMyPayout(period)),
      (payout) => dispatch(payoutActions.setPayout(payout)),
      { slot: 'payoutReducer.payout' },
    );
  } catch (e) {
    console.log(e);
  }
};

export const getPayoutGrid = (all?: boolean): AsyncAction => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    await revalidate(
      all ? '/payout/grid?all=1' : '/payout/grid',
      () => mainProtectedApi.getPayoutGrid(all),
      (grid) => dispatch(payoutActions.setGrid(grid)),
      { slot: 'payoutReducer.grid' },
    );
  } catch (e) {
    console.log(e);
  }
};

export const addContractor = (
  name: string,
  all?: boolean,
): AsyncAction => async (dispatch, _, { mainProtectedApi }) => {
  try {
    await mainProtectedApi.addContractor({ name });

    dispatch(getPayoutGrid(all));
  } catch (e) {
    console.log(e);
  }
};

export const setContractorCell = (
  contractorId: number,
  period: string,
  amount: number | null,
  all?: boolean,
): AsyncAction => async (dispatch, _, { mainProtectedApi }) => {
  try {
    await mainProtectedApi.setContractorCell(contractorId, { period, amount });

    dispatch(getPayoutGrid(all));
  } catch (e) {
    console.log(e);
  }
};

export const archiveContractor = (
  contractorId: number,
  archived: boolean,
  all?: boolean,
): AsyncAction => async (dispatch, _, { mainProtectedApi }) => {
  try {
    await mainProtectedApi.archiveContractor(contractorId, archived);

    await dispatch(getPayoutGrid(all));
  } catch (e) {
    console.log(e);
  }
};

export const renameContractor = (
  contractorId: number,
  name: string,
  all?: boolean,
): AsyncAction => async (dispatch, _, { mainProtectedApi }) => {
  try {
    await mainProtectedApi.renameContractor(contractorId, { name });

    await dispatch(getPayoutGrid(all));
  } catch (e) {
    console.log(e);
  }
};

export const getSalary = (userId: number): AsyncAction => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    await revalidate(
      `/salary/user/${userId}`,
      () => mainProtectedApi.getSalary(userId),
      (salary) => dispatch(payoutActions.setSalary(salary)),
      { slot: 'payoutReducer.salary' },
    );
  } catch (e) {
    console.log(e);
  }
};

export const changeSalary = (
  userId: number,
  amount: number,
  comment?: string,
): AsyncAction => async (dispatch, _, { mainProtectedApi }) => {
  try {
    await mainProtectedApi.changeSalary(userId, { amount, comment });

    dispatch(getSalary(userId));
  } catch (e) {
    console.log(e);
  }
};

export const deleteSalaryRecord = (
  id: number,
  userId: number,
): AsyncAction => async (dispatch, _, { mainProtectedApi }) => {
  try {
    await mainProtectedApi.deleteSalaryRecord(id);

    dispatch(getSalary(userId));
  } catch (e) {
    console.log(e);
  }
};

export const getSuggestions = (query?: string): AsyncAction => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    const suggestions = await mainProtectedApi.getExpenseSuggestions(query);

    dispatch(payoutActions.setSuggestions(suggestions));
  } catch (e) {
    console.log(e);
  }
};

const asFormData = (files: File[]) => {
  const form = new FormData();
  files.forEach((file) => form.append('files', file, file.name));

  return form;
};

/**
 * Files are optional. They go up once the expense exists, since they need its id;
 * a recurring one gets them on its occurrence for the month it starts in. If the
 * upload is refused the expense still stands -- the server's reason reaches the
 * global toast -- and the proof can be attached from the line afterwards.
 */
export const addExpense = (
  body: { title: string, amount: number, currency?: Currency, isRecurring?: boolean },
  target: PayoutTarget = {},
  files: File[] = [],
): AsyncAction => async (dispatch, _, { mainProtectedApi, mainProtectedFormDataApi }) => {
  try {
    const created = (target.userId
      ? await mainProtectedApi.addUserExpense(target.userId, body)
      : await mainProtectedApi.addOwnExpense(body)) as unknown as { _id: number, startPeriod?: string };

    if (files.length) {
      try {
        if (body.isRecurring && created.startPeriod) {
          await mainProtectedFormDataApi.attachToRecurring(
            created._id,
            created.startPeriod,
            asFormData(files),
          );
        } else {
          await mainProtectedFormDataApi.attachToExpense(created._id, asFormData(files));
        }
      } catch (e) {
        console.log(e);
      }
    }

    await dispatch(getPayout(target));
  } catch (e) {
    console.log(e);
    if (target.onError) target.onError();
  }
};

export const attachFiles = (
  line: PayoutLine,
  files: File[],
  period: string,
  target: PayoutTarget = {},
): AsyncAction => async (dispatch, _, { mainProtectedFormDataApi }) => {
  try {
    if (line.expenseId) {
      await mainProtectedFormDataApi.attachToExpense(line.expenseId, asFormData(files));
    } else if (line.recurringId) {
      await mainProtectedFormDataApi.attachToRecurring(line.recurringId, period, asFormData(files));
    }

    await dispatch(getPayout(target));
  } catch (e) {
    console.log(e);
    if (target.onError) target.onError();
  }
};

export const removeAttachment = (
  attachment: ExpenseAttachment,
  target: PayoutTarget = {},
): AsyncAction => async (dispatch, _, { mainProtectedApi }) => {
  try {
    await mainProtectedApi.deleteExpenseAttachment(attachment._id);
    forgetAttachment(attachment._id);

    await dispatch(getPayout(target));
  } catch (e) {
    console.log(e);
    if (target.onError) target.onError();
  }
};

export const deleteOwnExpense = (
  id: number,
  target: PayoutTarget = {},
): AsyncAction => async (dispatch, _, { mainProtectedApi }) => {
  try {
    await mainProtectedApi.deleteOwnExpense(id);

    dispatch(getPayout(target));
  } catch (e) {
    console.log(e);
    if (target.onError) target.onError();
  }
};

/**
 * A recurring expense has no row of its own until an admin acts on it, so the
 * line carries either an expenseId or only a recurringId, and the call to make
 * differs. The screen just says "defer this line".
 */
export const deferLine = (
  line: PayoutLine,
  period: string,
  target: PayoutTarget = {},
): AsyncAction => async (dispatch, _, { mainProtectedApi }) => {
  try {
    if (line.expenseId) {
      await mainProtectedApi.deferExpense(line.expenseId, period);
    } else if (line.recurringId) {
      await mainProtectedApi.deferRecurringExpense(line.recurringId, period);
    }

    dispatch(getPayout(target));
  } catch (e) {
    console.log(e);
    if (target.onError) target.onError();
  }
};

export const excludeLine = (
  line: PayoutLine,
  period: string,
  target: PayoutTarget = {},
): AsyncAction => async (dispatch, _, { mainProtectedApi }) => {
  try {
    if (line.expenseId) {
      await mainProtectedApi.excludeExpense(line.expenseId);
    } else if (line.recurringId) {
      await mainProtectedApi.excludeRecurringExpense(line.recurringId, period);
    }

    dispatch(getPayout(target));
  } catch (e) {
    console.log(e);
    if (target.onError) target.onError();
  }
};

export const restoreLine = (
  line: PayoutLine,
  target: PayoutTarget = {},
): AsyncAction => async (dispatch, _, { mainProtectedApi }) => {
  try {
    if (line.expenseId) await mainProtectedApi.restoreExpense(line.expenseId);

    dispatch(getPayout(target));
  } catch (e) {
    console.log(e);
    if (target.onError) target.onError();
  }
};

// Ends the rule from the next period on, leaving the period it was stopped in
// intact.
export const stopRecurringLine = (
  line: PayoutLine,
  period: string,
  target: PayoutTarget = {},
): AsyncAction => async (dispatch, _, { mainProtectedApi }) => {
  try {
    if (line.recurringId) {
      await mainProtectedApi.stopRecurringExpense(line.recurringId, period);
    }

    dispatch(getPayout(target));
  } catch (e) {
    console.log(e);
    if (target.onError) target.onError();
  }
};

// Whose payment details: a CRM user or a contractor, the same split as the
// grid's rows.
export type RequisitePayee = { userId: number } | { contractorId: number };

export const payeeRef = (payee: RequisitePayee) => (
  'userId' in payee ? `u:${payee.userId}` : `c:${payee.contractorId}`
);

const payeePath = (payee: RequisitePayee) => (
  'userId' in payee ? `user/${payee.userId}` : `contractor/${payee.contractorId}`
);

export const getRequisites = (payee: RequisitePayee): AsyncAction => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    await revalidate(
      `/requisites/${payeePath(payee)}`,
      () => mainProtectedApi.getRequisites(payeePath(payee)),
      (requisite) => dispatch(payoutActions.setRequisites({
        payee: payeeRef(payee),
        requisite: requisite || null,
      })),
      { slot: 'payoutReducer.requisites' },
    );
  } catch (e) {
    console.log(e);
  }
};

/**
 * Both mutations resolve to whether the write landed, so the form can stay open
 * with the admin's typing intact when it did not. Each awaits the refetch too:
 * the form closes on success, and the section behind it has to show the change
 * by then.
 */
const writeRequisites = (
  payee: RequisitePayee,
  write: (api: MainProtected) => Promise<unknown>,
): AsyncAction<Promise<boolean>> => async (dispatch, _, { mainProtectedApi }) => {
  try {
    await write(mainProtectedApi);
  } catch (e) {
    console.log(e);
    return false;
  }

  await dispatch(getRequisites(payee));
  return true;
};

export const saveRequisites = (payee: RequisitePayee, body: RequisiteBody) => (
  writeRequisites(payee, (api) => api.saveRequisites(payeePath(payee), body))
);

export const deleteRequisites = (payee: RequisitePayee) => (
  writeRequisites(payee, (api) => api.deleteRequisites(payeePath(payee)))
);

export const cleanPayout = (): AsyncAction => async (dispatch) => {
  dispatch(payoutActions.cleanPayout());
};
